import { useGlobalI18n } from "@/translations/i18n";

import { dayDiff, minuteDiff, startOfDay } from "./date";

type FormatterMap = Record<string, Intl.DateTimeFormat>;

const { t } = useGlobalI18n();

const timeFormatters: { time: FormatterMap; offset: FormatterMap } = { time: {}, offset: {} };

/**
 * Get a time formatter for a specific time zone. If the time zone is invalid, UTC is used.
 */
export function getTimeFormatterForZone(zone?: string) {
  const z = zone && isValidTimeZone(zone) ? zone : "UTC";

  if (timeFormatters.time[z]) {
    return timeFormatters.time[z];
  }

  const options = { hour: "2-digit", minute: "2-digit", timeZone: z } as const;
  timeFormatters.time[z] = new Intl.DateTimeFormat(navigator.languages as string[], options);

  return timeFormatters.time[z];
}

function isValidTimeZone(timeZone: string) {
  try {
    return !!Intl.DateTimeFormat(undefined, { timeZone: timeZone });
  } catch (_) {
    return false;
  }
}

// Eg. "2023-12-22"
export function toIsoDateString(date: Date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function fromIsoDateString(s: string): Date {
  const [year, month, day] = s.split("-").map(Number);
  return new Date(year, month - 1, day);
}

export function fromUtc(timestamp: number): Date {
  return new Date(timestamp * 1000);
}

// Eg. "22.12.2023"
export function toDateString(date: Date) {
  return date.toLocaleDateString(navigator.languages, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
}

// Eg. "22. Dez."
export function toShortDateString(date: Date) {
  return date.toLocaleDateString(navigator.languages, { month: "short", day: "numeric" });
}

// Eg. "22.12.23, 14:15"
export function toUtcDateTimeString(date: Date) {
  return date.toLocaleString(navigator.languages, {
    year: "2-digit",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "UTC",
  });
}

// Eg. "Di., 22. Dezember 2023 um 15:15 GMT+1"
export function toLongDateTimeString(date: Date) {
  return date.toLocaleString(navigator.languages, {
    year: "numeric",
    month: "long",
    day: "numeric",
    weekday: "short",
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "shortOffset",
  });
}

// Eg. "15:15"
export function toTimeString(date: Date) {
  return date.toLocaleString(navigator.languages, { hour: "2-digit", minute: "2-digit" });
}

// Eg. "14:05"
export function toUtcTimeString(date: Date) {
  return date.toLocaleString(navigator.languages, {
    hour: "2-digit",
    hourCycle: "h23",
    minute: "2-digit",
    timeZone: "UTC",
  });
}

// Eg. "14:05:09.042"
export function toUtcLongTimeString(date: Date) {
  return date.toLocaleString(navigator.languages, {
    hour: "2-digit",
    hourCycle: "h23",
    minute: "2-digit",
    second: "2-digit",
    fractionalSecondDigits: 3,
    timeZone: "UTC",
  });
}

const relativeDateName = [t("date.yesterday"), t("date.today"), t("date.tomorrow")];

export function relativeDate(date: Date) {
  const diff = dayDiff(startOfDay(new Date()), date) + 1;
  return diff < 0 || diff >= 3 ? toDateString(date) : relativeDateName[Math.floor(diff)];
}

export function relativeTime(date: Date) {
  const diff = minuteDiff(new Date(), date);
  const key = diff < 0 ? /*$t*/ "date.ago" : /*$t*/ "date.in";
  const value = Math.abs(diff);
  if (value >= 90) {
    return toTimeString(date);
  }
  if (value >= 60) {
    return t(key, {
      time: t("date.hour", { count: Math.round(value / 60) }),
    });
  }
  if (value >= 1) {
    return t(key, { time: t("date.minute", { count: Math.round(value) }) });
  }
  return t("date.now");
}
